import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, LogOut, Download, FileText, ImageIcon, Folder } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth, clientApi } from "@/contexts/ClientAuthContext";
import { API } from "@/lib/api";

function fileIcon(ct) {
  if (ct?.startsWith("image/")) return <ImageIcon size={18} />;
  return <FileText size={18} />;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

export default function ClientPortal() {
  const { client, ready, logout } = useClientAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready && client) {
      clientApi.get("/client/documents")
        .then(({ data }) => setDocs(data))
        .catch(() => toast.error("Erreur de chargement des documents"))
        .finally(() => setLoading(false));
    }
  }, [ready, client]);

  if (ready === false) return null;
  if (ready && !client) return <Navigate to="/espace-client/login" replace />;

  const download = async (id, filename) => {
    try {
      const token = localStorage.getItem("eq_client_token");
      const res = await fetch(`${API}/client/documents/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erreur");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Échec du téléchargement");
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-client-portal">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Espace personnel</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
              Bonjour {client?.name || ""}
            </h1>
            <p className="mt-2 text-zinc-400 text-sm">
              Retrouvez ici les documents que notre équipe a mis à votre disposition.
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="client-logout"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 text-sm hover:bg-white/5"
          >
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>

        <div className="mt-10 rounded-xl border border-white/10 overflow-hidden bg-[#0e0e0e]" data-testid="client-docs">
          {loading ? (
            <div className="p-12 text-center text-zinc-400">
              <Loader2 className="inline animate-spin mr-2" /> Chargement…
            </div>
          ) : docs.length === 0 ? (
            <div className="p-16 text-center text-zinc-500" data-testid="client-empty">
              <Folder size={32} className="mx-auto mb-3 text-zinc-700" />
              Aucun document à votre disposition pour le moment.
            </div>
          ) : (
            <ul>
              {docs.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-4 px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                  data-testid={`client-doc-${d.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-white/5 flex items-center justify-center text-zinc-300 flex-shrink-0">
                    {fileIcon(d.content_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{d.label}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {d.filename} · {formatSize(d.size)}
                      {d.category && ` · ${d.category}`}
                      {" · "}
                      {new Date(d.created_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <button
                    onClick={() => download(d.id, d.filename)}
                    data-testid={`client-doc-download-${d.id}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white text-black text-sm font-semibold"
                  >
                    <Download size={14} /> Télécharger
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
